import { Navigate, Route, Routes, type RouteObject } from 'react-router-dom';
import { BasePathContext } from './basePath';
import { ToastStack } from '@/components/arc/toast-stack/toast-stack';
import { JobsDrawer } from '@/components/jobs/JobsDrawer';
import { SourcesPage } from '@/pages/sources/SourcesPage';
import { VideosPage } from '@/pages/videos/VideosPage';
import { VideoDetailPage } from '@/pages/videos/VideoDetailPage';
import { ExplorePage } from '@/pages/explore/ExplorePage';
import { SegmentsPage } from '@/pages/segments/SegmentsPage';
import { SegmentEditorPage } from '@/pages/segments/SegmentEditorPage';
import { SegmentDetailPage } from '@/pages/segments/SegmentDetailPage';
import { OverlayLayout } from '@/pages/overlay/OverlayLayout';
import { PublishersPage } from '@/pages/overlay/PublishersPage';
import { CreativesPage } from '@/pages/overlay/CreativesPage';
import { CampaignsPage } from '@/pages/overlay/CampaignsPage';
import { CampaignStatsPage } from '@/pages/overlay/CampaignStatsPage';
import { DiscoveryPage } from '@/pages/overlay/DiscoveryPage';
import { CostsPage } from '@/pages/account/CostsPage';
import { ApiKeysPage } from '@/pages/account/ApiKeysPage';

/**
 * Route table (paths relative to the module base). Usable with `createBrowserRouter` in a host app:
 * `{ path: '/moment/*', children: scenewiseRoutes }` or render `<MomentModule basePath="/moment" />`.
 */
export const scenewiseRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="videos" replace /> },
  { path: 'sources', element: <SourcesPage /> },
  { path: 'videos', element: <VideosPage /> },
  { path: 'videos/:id', element: <VideoDetailPage /> },
  { path: 'explore', element: <ExplorePage /> },
  { path: 'segments', element: <SegmentsPage /> },
  { path: 'segments/new', element: <SegmentEditorPage /> },
  { path: 'segments/:id', element: <SegmentDetailPage /> },
  { path: 'segments/:id/edit', element: <SegmentEditorPage /> },
  {
    path: 'overlay',
    element: <OverlayLayout />,
    children: [
      { index: true, element: <Navigate to="publishers" replace /> },
      { path: 'publishers', element: <PublishersPage /> },
      { path: 'creatives', element: <CreativesPage /> },
      { path: 'campaigns', element: <CampaignsPage /> },
    ],
  },
  { path: 'overlay/campaigns/:id/stats', element: <CampaignStatsPage /> },
  { path: 'discovery', element: <DiscoveryPage /> },
  { path: 'costs', element: <CostsPage /> },
  { path: 'api-keys', element: <ApiKeysPage /> },
  { path: '*', element: <Navigate to="videos" replace /> },
];

/** @deprecated use scenewiseRoutes */
export const momentRoutes = scenewiseRoutes;

function renderRoutes(routes: RouteObject[]) {
  return routes.map((r, i) => (
    <Route key={r.path ?? `idx-${i}`} index={r.index as false | undefined} path={r.path} element={r.element}>
      {r.children ? renderRoutes(r.children) : null}
    </Route>
  ));
}

export interface MomentModuleProps {
  /** Absolute path the module is mounted under (used to build links). Default `/moment`. */
  basePath?: string;
  /** Render the jobs list (default true). */
  jobsDrawer?: boolean;
}

/**
 * Renders the Scenewise pages. Mount it under a splat route in the host router:
 * `<Route path="/moment/*" element={<MomentModule basePath="/moment" />} />`.
 * Must be inside a `<MomentProvider>` and a react-router `Router`.
 */
export function MomentModule({ basePath = '/moment', jobsDrawer = true }: MomentModuleProps) {
  return (
    <BasePathContext.Provider value={basePath.replace(/\/+$/, '') || '/'}>
      <Routes>{renderRoutes(scenewiseRoutes)}</Routes>
      {jobsDrawer && <JobsDrawer />}
      <ToastStack />
    </BasePathContext.Provider>
  );
}
